import React from "react";
import { NavLink } from "react-router-dom";
import {
    FiChevronsLeft,
    FiChevronsRight,
    FiFileText,
    FiGrid,
    FiLogOut,
    FiShield,
    FiUser,
    FiX,
} from "react-icons/fi";

import useAuth from "../hooks/useAuth";
import { ADMIN_PERMISSION_KEYS } from "../constants/adminModules";
import { APP_MODULES, COMPANY_PANEL_MODULE } from "../constants/appModules";
import { USER_TYPE_LABELS } from "../constants/userTypes";
import { useUI } from "../context/UIContext";
import { localizedPath } from "../utils/routeHelpers";
import styles from "./Sidebar.module.css";

/**
 * Menu osoby ZEWNĘTRZNEJ (konto z portalu, zakładka „Kartoteka wizytatorów").
 *
 * Taka osoba widzi w aplikacji te same dwa ekrany co pracownik — pulpit i przepustki —
 * ale wchodzi do systemu po to, żeby złożyć wniosek i sprawdzić swój, a nie żeby
 * obsługiwać cudze. Dlatego dostaje wprost te pozycje, które ma portal `/wniosek`
 * (nowy wniosek + moje konto): jedno menu na obu powierzchniach zamiast dwóch
 * rozjeżdżających się nawigacji.
 *
 * `external: true` = trasa spoza ramy aplikacji (portal renderuje się bez Layoutu),
 * więc podajemy pełną ścieżkę zamiast klucza z routes.js.
 */
// Menu wizytatora: DWA wpisy (decyzja klienta 2026-08-08). Pulpit jest jego domem —
// tam otwiera formularz wniosku (modal jednego wzoru), widzi historię swoich przepustek
// i swoje dane. Osobne linki do portalu (/wniosek, /wniosek/profil) poszły do kosza:
// portal publiczny służy wyłącznie osobom BEZ konta.
const PORTAL_SECTIONS = [
    {
        title: "MOJE PRZEPUSTKI",
        items: [
            { key: "dashboard", label: "Pulpit", icon: FiGrid, end: true },
            { key: "passes", label: "Moje wnioski", icon: FiFileText },
            // AKCJA, nie trasa: otwiera wysuwane okno danych konta niezaleznie od tego,
            // gdzie uzytkownik jest (drawer mieszka w Layout, nasluchuje zdarzenia).
            { action: "bp:open-profile", label: "Moje dane", icon: FiUser },
        ],
    },
];

/**
 * Układ menu 1:1 z Serwisem FTP (a więc i z mPracownikiem): logo Krosno na górze, karta
 * użytkownika, sekcje z mikro-nagłówkami, stopka z adresem kontaktowym i wylogowaniem.
 *
 * POZYCJE codziennej pracy przychodzą z katalogu appModules.js — tego samego, z którego
 * pulpit buduje kafelki „Szybkiego dostępu". Dzięki temu menu i pulpit pokazują ZAWSZE
 * ten sam zestaw modułów danej osoby (ustalenie klienta 2026-08-08); tu dochodzi tylko
 * sekcja ADMINISTRACJA, bo panel administratora jest osobną pozycją, nie modułem katalogu.
 */
const SECTIONS = [
    {
        title: "BIURO PRZEPUSTEK",
        items: APP_MODULES.map((module) => ({
            key: module.routeKey,
            label: module.label,
            icon: module.icon,
            permission: module.permission,
            end: module.end,
        })),
    },
    {
        title: "FIRMA",
        items: [
            // Panel firmy — pozycja na wzór panelu administratora (osobna sekcja, własna
            // bramka), tyle że bramką nie jest uprawnienie, a powiązanie konta z firmą:
            // `companyOnly` filtruje po `user.companyId` (patrz filtr sekcji niżej).
            // Widzi ją przedstawiciel firmy (Type=5) ORAZ pracownik KG przypięty do firmy.
            {
                key: COMPANY_PANEL_MODULE.routeKey,
                label: COMPANY_PANEL_MODULE.label,
                icon: COMPANY_PANEL_MODULE.icon,
                companyOnly: true,
            },
        ],
    },
    {
        title: "ADMINISTRACJA",
        items: [
            // JEDNA pozycja zamiast ośmiu. Moduły konfiguracyjne żyją teraz w panelu pod /admin:
            // menu boczne opisuje CODZIENNĄ pracę, a rzeczy, które robi się raz na kwartał,
            // nie mają zajmować w nim ośmiu wierszy. Panel dodatkowo tłumaczy każdy moduł
            // i pokazuje, które czynności są dla danej osoby dostępne.
            //
            // Pozycję widzi każdy, kto ma choć jedno uprawnienie administracyjne —
            // lista kluczy pochodzi z tego samego katalogu, z którego panel buduje kafelki.
            {
                key: "adminPanel",
                label: "Panel administratora",
                icon: FiShield,
                permission: ADMIN_PERMISSION_KEYS,
                end: true,
            },
        ],
    },
];

const Sidebar = () => {
    const { user, hasPermission, logout } = useAuth();
    const { isMobile, isSidebarCollapsed, isMobileSidebarOpen, toggleSidebar, closeMobileSidebar } = useUI();

    const collapsed = isSidebarCollapsed && !isMobile;
    const initials = `${user?.firstName?.[0] || ""}${user?.lastName?.[0] || ""}`.toUpperCase() || "?";
    const fullName = `${user?.firstName || ""} ${user?.lastName || ""}`.trim();

    // Konto portalowe (wiersz w PortalAccounts) = ranga WIZYTATOR: osoba składająca wniosek,
    // nie pracownik Biura Przepustek — dostaje własne menu i własny podpis rangi.
    const isPortal = Boolean(user?.isPortalAccount);
    const sections = isPortal ? PORTAL_SECTIONS : SECTIONS;
    // Przedstawiciel firmy (Type=5) nie ma działu ani wpisu w USER_TYPE_LABELS — bez tej
    // gałęzi spadałby na mylące „Obsługa". Pracownik KG przypięty do firmy zachowuje
    // podpis działu (warunek companyId stoi ZA departmentName w łańcuchu wyboru).
    const roleLabel = isPortal
        ? "Wizytator"
        : user?.departmentName ||
          USER_TYPE_LABELS[user?.type] ||
          (user?.companyId ? "Przedstawiciel firmy" : "Obsługa");

    // logout() z AuthContext sam odsyła na ekran logowania.
    const handleLogout = () => {
        closeMobileSidebar();
        logout();
    };

    const classes = [
        styles.sidebar,
        collapsed ? styles.collapsed : "",
        isMobileSidebarOpen ? styles.mobileOpen : "",
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <>
            <aside className={classes}>
                <div className={styles.sidebarHeader}>
                    {!collapsed && (
                        <NavLink
                            to={localizedPath("dashboard")}
                            className={styles.logoLink}
                            onClick={closeMobileSidebar}
                        >
                            <img src="/logo.png" alt="Krosno Glass" className={styles.logo} />
                        </NavLink>
                    )}
                    {isMobile ? (
                        <button
                            type="button"
                            className={styles.toggleBtn}
                            onClick={closeMobileSidebar}
                            title="Zamknij menu"
                            aria-label="Zamknij menu"
                        >
                            <FiX />
                        </button>
                    ) : (
                        <button
                            type="button"
                            className={styles.toggleBtn}
                            onClick={toggleSidebar}
                            title={collapsed ? "Rozwiń menu" : "Zwiń menu"}
                            aria-label={collapsed ? "Rozwiń menu" : "Zwiń menu"}
                        >
                            {collapsed ? <FiChevronsRight /> : <FiChevronsLeft />}
                        </button>
                    )}
                </div>

                {collapsed ? (
                    <div className={styles.userCardCollapsed}>
                        <div className={styles.userAvatarSmall} title={fullName || user?.email}>
                            <span>{initials}</span>
                        </div>
                    </div>
                ) : (
                    <div className={styles.userCard}>
                        <div className={styles.userAvatar}><span>{initials}</span></div>
                        <div className={styles.userMeta}>
                            <span className={styles.userCardName}>{fullName || user?.email || "…"}</span>
                            <span className={styles.userCardRole}>{roleLabel}</span>
                        </div>
                    </div>
                )}

                <nav className={styles.nav}>
                    {sections.map((section) => {
                        // Pozycja znika, gdy brakuje uprawnienia — dokładnie tak, jak trasę zamyka
                        // ProtectedRoute; menu nie może prowadzić na ekran „Brak dostępu".
                        // `companyOnly` to druga, równoległa bramka: pozycję „Panel firmy"
                        // widzi wyłącznie konto powiązane z firmą (user.companyId z /users/me) —
                        // lustro bramki samej strony /firma.
                        const visible = section.items.filter(
                            (item) =>
                                (!item.permission || hasPermission(item.permission)) &&
                                (!item.companyOnly || Boolean(user?.companyId))
                        );
                        if (visible.length === 0) {
                            return null;
                        }
                        return (
                            <div key={section.title} className={styles.section}>
                                {collapsed ? (
                                    <div className={styles.sectionDivider} />
                                ) : (
                                    <p className={styles.sectionTitle}>{section.title}</p>
                                )}
                                <ul className={styles.sectionList}>
                                    {visible.map((item) => {
                                        const Icon = item.icon;
                                        // Pozycja-AKCJA (np. „Moje dane"): przycisk wysyłający
                                        // zdarzenie — okno otwiera Layout, niezależnie od trasy.
                                        if (item.action) {
                                            return (
                                                <li key={item.action}>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            closeMobileSidebar();
                                                            window.dispatchEvent(new CustomEvent(item.action));
                                                        }}
                                                        title={collapsed ? item.label : undefined}
                                                        className={[
                                                            styles.navLink,
                                                            styles.navAction,
                                                            collapsed ? styles.navLinkCollapsed : "",
                                                        ]
                                                            .filter(Boolean)
                                                            .join(" ")}
                                                    >
                                                        <span className={styles.navIcon}><Icon /></span>
                                                        {!collapsed && (
                                                            <span className={styles.navLabel}>{item.label}</span>
                                                        )}
                                                    </button>
                                                </li>
                                            );
                                        }
                                        return (
                                            <li key={item.key || item.path}>
                                                <NavLink
                                                    to={item.external ? item.path : localizedPath(item.key)}
                                                    end={item.end}
                                                    onClick={closeMobileSidebar}
                                                    title={collapsed ? item.label : undefined}
                                                    className={({ isActive }) =>
                                                        [
                                                            styles.navLink,
                                                            collapsed ? styles.navLinkCollapsed : "",
                                                            isActive ? styles.active : "",
                                                        ]
                                                            .filter(Boolean)
                                                            .join(" ")
                                                    }
                                                >
                                                    <span className={styles.navIcon}><Icon /></span>
                                                    {!collapsed && <span className={styles.navLabel}>{item.label}</span>}
                                                </NavLink>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        );
                    })}
                </nav>

                <div className={styles.sidebarFooter}>
                    {!collapsed && (
                        <div className={styles.footerMeta}>
                            <span>Biuro Przepustek · Krosno Glass S.A.</span>
                            <a href="mailto:biuroprzepustek@krosno.com">biuroprzepustek@krosno.com</a>
                        </div>
                    )}
                    <button
                        type="button"
                        onClick={handleLogout}
                        className={`${styles.logoutBtn} ${collapsed ? styles.logoutBtnCollapsed : ""}`}
                        title="Wyloguj się"
                    >
                        <FiLogOut />
                        {!collapsed && <span>Wyloguj się</span>}
                    </button>
                </div>
            </aside>

            {isMobile && isMobileSidebarOpen && (
                <div className={styles.overlay} onClick={closeMobileSidebar} />
            )}
        </>
    );
};

export default Sidebar;
